// What a re-issued drawing proposes about the zones already on the job.
//
// Re-uploading an area sheet used to produce a second, parallel set of
// zones: the old ones kept every link to the schedule, the issues and
// the checklists; the new ones started empty; nothing reconciled them.
// This is where that gets reconciled, and it is a REVIEW - the pairing
// is computed and shown with the reason it was made, row by row, and
// none of it happens until the owner says so.
//
// Retiring a zone does not delete it. It becomes historic: off the live
// drawing, still readable as the map that was current at the time.
import React, { useMemo, useState } from "react";
import { Button, Select, Tag, Tooltip, Popconfirm, message } from "antd";
import { ArrowRightOutlined, HistoryOutlined } from "@ant-design/icons";
import AreaShapePreview from "components/Coordination/AreaShapePreview";
import { decideAreaMigration } from "api/areas/areasApi";

const keyOf = (r) => `${r.oldAreaId || ""}|${r.newAreaId || ""}`;

// The choices a reviewer actually has, which depend on what the row IS.
const optionsFor = (row) => {
  if (row.oldAreaId && row.newAreaId) {
    return [
      { value: "carry", label: "Replaces it — carry the work over" },
      { value: "separate", label: "Different zones — keep both" },
      { value: "skip", label: "Decide later" },
    ];
  }
  if (row.oldAreaId) {
    return [
      { value: "retire", label: "Off the new sheet — retire it" },
      { value: "skip", label: "Leave it as it is" },
    ];
  }
  return [
    { value: "add", label: "New zone — put it on the drawing" },
    { value: "skip", label: "Decide later" },
  ];
};

const Swatch = ({ color }) => (
  <span
    style={{
      width: 12, height: 12, borderRadius: 3, display: "inline-block",
      background: color || "#cbd5e1", flex: "0 0 auto",
    }}
  />
);

// Name + colour, with the shape itself on hover: a zone name says
// nothing about WHERE on the floor it is.
const AreaChip = ({ area, name, muted }) => {
  if (!name) return <span style={{ color: "#cbd5e1" }}>—</span>;
  const body = (
    <span
      className="d-inline-flex align-items-center"
      style={{ gap: 6, textDecoration: muted ? "line-through" : "none", color: muted ? "#94a3b8" : "inherit" }}
    >
      <Swatch color={area?.color} />
      <span style={{ fontSize: 13 }}>{name}</span>
      {area && area.revision > 1 && <Tag style={{ marginInlineEnd: 0 }}>rev {area.revision}</Tag>}
    </span>
  );
  if (!area || !(area.points || []).length) return body;
  return (
    <Tooltip
      color="#0f172a"
      title={<AreaShapePreview area={area} zoom width={220} height={150} showDates={false} />}
    >
      {body}
    </Tooltip>
  );
};

const moves = (c) => [
  c.activities ? `${c.activities} ${c.activities === 1 ? "activity" : "activities"}` : "",
  c.issues ? `${c.issues} ${c.issues === 1 ? "issue" : "issues"}` : "",
  c.checklists ? `${c.checklists} checklist${c.checklists === 1 ? "" : "s"}` : "",
  c.milestones ? `${c.milestones} milestone${c.milestones === 1 ? "" : "s"}` : "",
].filter(Boolean).join(" · ");

export default function AreaMapRevisionPanel({ projectId, migration, onDone }) {
  // Only the rows the reviewer CHANGED are sent; everything else keeps
  // the action the proposal came with.
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => (migration?.rows || []).map((r) => ({
    ...r,
    action: overrides[keyOf(r)] || r.action,
  })), [migration, overrides]);

  const tally = useMemo(() => {
    const t = { carry: 0, separate: 0, retire: 0, add: 0, skip: 0 };
    rows.forEach((r) => { t[r.action] = (t[r.action] || 0) + 1; });
    return t;
  }, [rows]);

  if (!migration) return null;

  const send = async (decision) => {
    setBusy(true);
    try {
      const body = Object.keys(overrides).length
        ? rows.map((r) => ({ oldAreaId: r.oldAreaId, newAreaId: r.newAreaId, action: r.action }))
        : undefined;
      const r = await decideAreaMigration(projectId, migration._id, decision, body);
      if (decision === "discard") {
        message.success("Mapping discarded — nothing changed");
      } else {
        const s = r.summary || {};
        message.success(
          `${s.carried || 0} zone(s) replaced, ${s.retired || 0} retired, ${s.added || 0} added`
          + (s.activities || s.issues || s.checklists
            ? ` — ${moves(s)} carried over`
            : "")
        );
      }
      setOverrides({});
      if (onDone) onDone();
    } catch (e) {
      message.error(e.message || "Could not apply the mapping");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6,
        padding: "12px 14px", marginBottom: 14,
      }}
    >
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
        <HistoryOutlined style={{ color: "#1d4ed8" }} />
        <b>A newer drawing</b>
        {migration.sheetName && <Tag color="blue">{migration.sheetName}</Tag>}
        {migration.fromRevision != null && (
          <span style={{ color: "#1e40af", fontSize: 12 }}>
            rev {migration.fromRevision} → {migration.toRevision}
          </span>
        )}
      </div>
      <div style={{ color: "#1e40af", fontSize: 13, margin: "4px 0 10px" }}>
        Say which zone on the new sheet replaces which on the old one. Carrying a zone
        over moves its activities, issues and checklists to the new shape and files the
        old one as history — it is retired, never deleted, so the map that was current
        at the time still reads.
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: "#64748b", textAlign: "left" }}>
              <th style={{ padding: "4px 8px 4px 0", fontWeight: 500 }}>On the old sheet</th>
              <th style={{ padding: "4px 8px", fontWeight: 500 }} />
              <th style={{ padding: "4px 8px", fontWeight: 500 }}>On the new one</th>
              <th style={{ padding: "4px 8px", fontWeight: 500 }}>Why</th>
              <th style={{ padding: "4px 8px", fontWeight: 500 }}>What moves</th>
              <th style={{ padding: "4px 0 4px 8px", fontWeight: 500 }}>Decision</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={keyOf(r)} style={{ borderTop: "1px solid #dbeafe" }}>
                <td style={{ padding: "6px 8px 6px 0" }}>
                  <AreaChip area={r.oldArea} name={r.oldName} muted={r.action === "carry" || r.action === "retire"} />
                </td>
                <td style={{ padding: "6px 8px", color: "#94a3b8" }}>
                  {r.oldAreaId && r.newAreaId && r.action === "carry" ? <ArrowRightOutlined /> : null}
                </td>
                <td style={{ padding: "6px 8px" }}>
                  <AreaChip area={r.newArea} name={r.newName} />
                </td>
                <td style={{ padding: "6px 8px", color: "#64748b" }}>
                  {r.via || "—"}
                  {r.score ? <span style={{ color: "#94a3b8" }}> ({Math.round(r.score * 100)}%)</span> : null}
                </td>
                <td style={{ padding: "6px 8px", color: "#475569" }}>
                  {r.action === "carry" ? (moves(r.carries || {}) || "nothing filed under it yet") : "—"}
                </td>
                <td style={{ padding: "6px 0 6px 8px" }}>
                  <Select
                    size="small"
                    style={{ minWidth: 240 }}
                    value={r.action}
                    options={optionsFor(r)}
                    onChange={(v) => setOverrides((o) => ({ ...o, [keyOf(r)]: v }))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, marginTop: 12 }}>
        <Popconfirm
          title="Apply this mapping?"
          description={
            `${tally.carry} zone(s) replaced, ${tally.retire} retired, `
            + `${tally.add + tally.separate} added. Retired zones stay readable as history.`
          }
          okText="Apply"
          onConfirm={() => send("apply")}
        >
          <Button type="primary" loading={busy}>Apply mapping</Button>
        </Popconfirm>
        <Popconfirm
          title="Discard this mapping?"
          description="The new zones stay waiting for you to validate one by one, and nothing is retired."
          okText="Discard"
          onConfirm={() => send("discard")}
        >
          <Button danger disabled={busy}>Discard</Button>
        </Popconfirm>
        <span style={{ color: "#64748b", fontSize: 12 }}>
          {tally.skip ? `${tally.skip} row(s) left for later` : "Every row has a decision"}
        </span>
      </div>
    </div>
  );
}
